// src/components/Sidebar.jsx
// Brutalist navigation sidebar
import { NavLink } from 'react-router-dom';
import {
    HomeIcon,
    UsersIcon,
    BuildingOfficeIcon,
    DocumentTextIcon,
    CogIcon,
    UserGroupIcon,
    CreditCardIcon,
    PhoneIcon,
    ShieldCheckIcon,
    XMarkIcon,
    ServerIcon,
    DocumentMagnifyingGlassIcon,
    FingerPrintIcon,
    CircleStackIcon,
    SparklesIcon,
    EnvelopeIcon,
    BellIcon,
    WrenchScrewdriverIcon,
    BoltIcon,
    MegaphoneIcon,
} from '@heroicons/react/24/outline';

const Sidebar = ({ open, setOpen }) => {
    const navItems = [
        { name: 'DASHBOARD', to: '/dashboard', icon: HomeIcon },
        { name: 'SALES', to: '/sales', icon: PhoneIcon },
        { name: 'USERS', to: '/users', icon: UsersIcon },
        { name: 'COMPANIES', to: '/companies', icon: BuildingOfficeIcon },
        { name: 'INVITATIONS', to: '/team-invitations', icon: EnvelopeIcon },
        { name: 'REPORTS', to: '/saved-search-reports', icon: BellIcon },
        { name: 'ENGAGEMENT', to: '/engagement', icon: MegaphoneIcon },
        { name: 'TENDERS', to: '/tenders', icon: DocumentTextIcon },
        { name: 'OPERATIONS', to: '/operations', icon: WrenchScrewdriverIcon },
        { name: 'EVENT ACTIONS', to: '/event-actions', icon: BoltIcon },
        { name: 'DEEP CRAWL', to: '/deep-crawl', icon: DocumentMagnifyingGlassIcon },
        { name: 'BETA SIGNUPS', to: '/beta-signups', icon: UserGroupIcon },
        { name: 'SUBSCRIPTIONS', to: '/subscriptions', icon: CreditCardIcon },
        { name: 'FEATURES', to: '/features', icon: SparklesIcon },
        { name: 'SESSIONS', to: '/sessions', icon: FingerPrintIcon },
        { name: 'PERMISSIONS', to: '/permissions', icon: ShieldCheckIcon },
        { name: 'SYSTEM', to: '/system', icon: ServerIcon },
        { name: 'STORAGE', to: '/storage', icon: CircleStackIcon },
        { name: 'SETTINGS', to: '/settings', icon: CogIcon },
    ];

    return (
        <div className={`${open ? 'translate-x-0' : '-translate-x-full'} fixed inset-y-0 left-0 z-30 w-56 bg-white/80 backdrop-blur-xl border-r border-slate-200 transition duration-200 ease-in-out transform md:translate-x-0 md:static md:inset-0 flex flex-col`}>
            {/* Brand */}
            <div className="flex items-center justify-between h-14 px-4 border-b border-slate-200">
                <div className="flex items-center gap-2.5">
                    <span className="grid place-items-center h-7 w-7 rounded-lg bg-primary-gradient shadow-glow">
                        <span className="text-on-accent font-bold text-sm leading-none">B</span>
                    </span>
                    <div className="flex flex-col leading-none">
                        <span className="text-sm font-semibold text-slate-900 tracking-tight">BidLyzer</span>
                        <span className="text-[10px] font-mono text-slate-400 uppercase tracking-widest mt-0.5">Backstage</span>
                    </div>
                </div>
                <button
                    onClick={() => setOpen(false)}
                    className="md:hidden p-1 text-slate-400 hover:text-slate-900"
                    aria-label="Close sidebar"
                >
                    <XMarkIcon className="h-5 w-5" />
                </button>
            </div>

            {/* Navigation — soft primary-tinted active pill */}
            <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5">
                {navItems.map((item) => (
                    <NavLink
                        key={item.name}
                        to={item.to}
                        className={({ isActive }) =>
                            `group relative flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-mono tracking-wide transition-all duration-150 ${
                                isActive
                                    ? 'bg-primary/10 text-primary-dark font-semibold'
                                    : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
                            }`
                        }
                    >
                        {({ isActive }) => (
                            <>
                                <span className={`absolute left-0 top-1/2 -translate-y-1/2 h-5 w-1 rounded-full bg-primary transition-all duration-200 ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-30'}`} />
                                <item.icon className={`h-4 w-4 flex-shrink-0 transition-colors ${isActive ? 'text-primary' : 'text-slate-400 group-hover:text-slate-600'}`} />
                                {item.name}
                            </>
                        )}
                    </NavLink>
                ))}
            </nav>

            {/* Footer */}
            <div className="px-4 py-3 text-[10px] font-mono text-slate-400 border-t border-slate-200 flex items-center justify-between">
                <span className="uppercase tracking-widest">Admin Console</span>
                <span className="text-slate-300">v1.0.0</span>
            </div>
        </div>
    );
};

export default Sidebar;
